// Dispatcher: pick today's stops, plan van routes (same algorithm as route-planner.html),
// assign drivers, then save as draft or publish to drivers.
import { listLocations, listProfiles, createPlan, savePlan, todayIST } from '../lib/api.js';
import { osrmTable, osrmRoute } from '../lib/routing.js';
import {
  assignVans, routeOrder, computeEtas, fmtClock, fmtDuration, parseClock, validatePlan,
  VAN_COLORS, MAX_STOPS_PER_VAN,
} from '../lib/planner.js';
import { $, $$, esc, toast, busy, makeMap, numberIcon, L } from '../lib/ui.js';

const pref = (k, d) => { try { return localStorage.getItem(k) ?? d; } catch { return d; } };
const setPref = (k, v) => { try { localStorage.setItem(k, v); } catch { /* private mode */ } };

export async function renderPlan(app) {
  app.innerHTML = '<div class="side" id="side"><p class="pad">Loading locations…</p></div><div class="map" id="map"></div>';
  const map = makeMap($('#map'));
  const layer = L.layerGroup().addTo(map);
  const side = $('#side');

  let locations, drivers;
  try {
    [locations, drivers] = await Promise.all([listLocations(), listProfiles()]);
  } catch (e) { side.innerHTML = `<div class="err">${esc(e.message)}</div>`; return () => map.destroy(); }
  drivers = drivers.filter(p => p.role === 'driver' && p.active);
  const depot = locations.find(l => l.is_depot);
  const dests = locations.filter(l => !l.is_depot).map(l => ({ ...l, sel: false, van: 0, vol: 1, early: false }));

  const settings = {
    date: todayIST(),
    vans: +pref('acmeVanCount', '1'),
    depart: pref('acmeDepart', '08:00'),
    unload: +pref('acmeSvcMin', '10'),
  };
  let result = null; // last computed plan

  function drawPreview() {
    layer.clearLayers();
    const pts = [];
    locations.forEach(l => {
      if (!Number.isFinite(l.lat)) return;
      pts.push([l.lat, l.lng]);
      layer.addLayer(L.circleMarker([l.lat, l.lng], { radius: 6, color: '#fff', weight: 2, fillColor: l.is_depot ? '#2e8b57' : '#7a8bab', fillOpacity: 1 }).bindTooltip(esc(l.name)));
    });
    if (pts.length) { map.invalidateSize(); map.fitBounds(L.latLngBounds(pts).pad(0.15)); }
  }

  function showPicker() {
    result = null;
    drawPreview();
    if (!depot) { side.innerHTML = '<div class="err">No godown/depot set. Add one under Locations (Type = Source).</div>'; return; }
    const n = settings.vans;
    side.innerHTML = `
      <div class="bar">
        <button class="primary" id="planBtn" disabled>Plan route</button>
        <button id="all">All</button><button id="none">Clear</button>
        <label>Vans <select id="vans">${[1, 2, 3, 4, 5, 6].map(v => `<option ${v === n ? 'selected' : ''}>${v}</option>`).join('')}</select></label>
      </div>
      <div class="bar" style="top:auto;position:static">
        <label>Date <input type="date" id="date" value="${settings.date}"></label>
        <label>Depart <input type="time" id="dep" value="${settings.depart}"></label>
        <label>Unload <input type="number" id="svc" min="0" max="120" value="${settings.unload}"> min</label>
      </div>
      <div class="summary">🏭 <b>${esc(depot.name)}</b> (godown, always included)<br>${dests.length} locations. Tick today's stops; set van, volume or "early" if needed.</div>
      ${dests.map((d, i) => `
        <div class="row">
          <input type="checkbox" class="pick" data-i="${i}" ${d.sel ? 'checked' : ''} style="width:18px;height:18px;margin-top:2px">
          <div class="grow">
            <div class="name">${esc(d.name)}</div>
            <div class="addr">${esc(d.address)}${Number.isFinite(d.lat) ? '' : ' <b style="color:#b45309">(no GPS — fix under Locations)</b>'}</div>
            <div class="ctl" data-i="${i}" ${d.sel ? '' : 'hidden'}>
              <label>Van <select class="van" data-i="${i}" ${n === 1 ? 'disabled' : ''}>
                <option value="0">Auto</option>${Array.from({ length: n }, (_, v) => `<option value="${v + 1}" ${d.van === v + 1 ? 'selected' : ''}>Van ${v + 1}</option>`).join('')}
              </select></label>
              <label>Vol <input type="number" class="vol" data-i="${i}" min="1" value="${d.vol}"></label>
              <label><input type="checkbox" class="early" data-i="${i}" ${d.early ? 'checked' : ''}> deliver early</label>
            </div>
          </div>
        </div>`).join('')}`;

    const refresh = () => {
      const sel = dests.filter(d => d.sel);
      const vol = sel.reduce((a, d) => a + d.vol, 0);
      const btn = $('#planBtn');
      const tooMany = sel.length > MAX_STOPS_PER_VAN * settings.vans;
      btn.disabled = !sel.length || tooMany;
      btn.textContent = tooMany ? `Too many (max ${MAX_STOPS_PER_VAN * settings.vans})`
        : `Plan route (${sel.length}${settings.vans > 1 ? ` · ${settings.vans} vans` : ''}${vol !== sel.length ? ` · vol ${vol}` : ''})`;
    };
    $$('.pick', side).forEach(b => b.onchange = () => {
      dests[+b.dataset.i].sel = b.checked;
      side.querySelector(`.ctl[data-i="${b.dataset.i}"]`).hidden = !b.checked;
      refresh();
    });
    $$('.van', side).forEach(el => el.onchange = () => { dests[+el.dataset.i].van = +el.value; });
    $$('.vol', side).forEach(el => el.onchange = () => { dests[+el.dataset.i].vol = Math.max(1, +el.value || 1); refresh(); });
    $$('.early', side).forEach(el => el.onchange = () => { dests[+el.dataset.i].early = el.checked; });
    $('#vans').onchange = e => {
      settings.vans = +e.target.value; setPref('acmeVanCount', settings.vans);
      dests.forEach(d => { if (d.van > settings.vans) d.van = 0; });
      showPicker();
    };
    $('#date').onchange = e => { settings.date = e.target.value; };
    $('#dep').onchange = e => { settings.depart = e.target.value; setPref('acmeDepart', settings.depart); };
    $('#svc').onchange = e => { settings.unload = Math.max(0, +e.target.value || 0); setPref('acmeSvcMin', settings.unload); };
    $('#all').onclick = () => { dests.forEach(d => { d.sel = true; }); showPicker(); };
    $('#none').onclick = () => { dests.forEach(d => { d.sel = false; }); showPicker(); };
    $('#planBtn').onclick = e => busy(e.target, compute).catch(() => {});
    refresh();
  }

  async function compute() {
    const items = dests.filter(d => d.sel);
    const err = validatePlan(items, settings.vans);
    if (err) throw new Error(err);
    const departMin = parseClock(settings.depart);
    const clusters = assignVans(depot, items, Math.min(settings.vans, items.length));
    const vans = [];
    for (const c of clusters) {
      const pts = [depot, ...c.stops];
      const dist = await osrmTable(pts);
      const earlySet = new Set(pts.map((p, i) => (p.early ? i : -1)).filter(i => i > 0));
      const ordered = routeOrder(dist, earlySet).map(i => pts[i]);
      const route = await osrmRoute([...ordered, depot]);
      const etas = computeEtas(route.legs, departMin, settings.unload);
      vans.push({
        van_no: vans.length + 1, color: VAN_COLORS[vans.length % VAN_COLORS.length],
        stops: ordered.slice(1), etas, route,
        driver_id: drivers[vans.length]?.id || '',
        vol: c.stops.reduce((a, s) => a + s.vol, 0),
      });
    }
    result = { vans, departMin };
    showResult();
  }

  function showResult() {
    layer.clearLayers();
    const { vans } = result;
    const allPts = [[depot.lat, depot.lng]];
    layer.addLayer(L.marker([depot.lat, depot.lng], { icon: numberIcon('G', '#2e8b57') }).bindPopup(esc(depot.name)));
    const km = vans.reduce((a, v) => a + v.route.distance, 0) / 1000;
    const back = Math.max(...vans.map(v => v.etas.at(-1)));
    let html = `<div class="bar"><button id="back">← Back</button>
      <button id="draft">Save draft</button><button class="primary" id="publish">Publish to drivers</button></div>
      <div class="summary">📅 ${esc(settings.date)} · <b>${vans.length} van${vans.length > 1 ? 's' : ''}</b> · ${vans.reduce((a, v) => a + v.stops.length, 0)} stops · ${km.toFixed(1)} km · all back by <b>~${fmtClock(back)}</b></div>`;
    vans.forEach((v, vi) => {
      layer.addLayer(L.geoJSON(v.route.geometry, { style: { color: v.color, weight: 5, opacity: 0.8 } }));
      v.stops.forEach((s, i) => {
        allPts.push([s.lat, s.lng]);
        layer.addLayer(L.marker([s.lat, s.lng], { icon: numberIcon(vans.length > 1 ? `${v.van_no}.${i + 1}` : i + 1, v.color) })
          .bindPopup(`<b>Van ${v.van_no}, stop ${i + 1}: ${esc(s.name)}</b><br>${esc(s.address)}`));
      });
      html += `<div class="summary" style="border-left:6px solid ${v.color}">
        🚚 <b>Van ${v.van_no}</b> · ${v.stops.length} stops · vol ${v.vol} · ${(v.route.distance / 1000).toFixed(1)} km · ${fmtDuration(v.route.duration / 60)} driving · back ~<b>${fmtClock(v.etas.at(-1))}</b>
        <div class="ctl"><label>Driver <select class="drv" data-v="${vi}"><option value="">— not assigned —</option>
          ${drivers.map(d => `<option value="${d.id}" ${d.id === v.driver_id ? 'selected' : ''}>${esc(d.full_name || d.id.slice(0, 8))}</option>`).join('')}
        </select></label></div></div>`;
      v.stops.forEach((s, i) => {
        html += `<div class="row"><div class="badge" style="background:${v.color}">${i + 1}</div><div class="grow">
          <div class="name">${esc(s.name)}${s.early ? ' <span class="chip skipped">early</span>' : ''}</div>
          <div class="addr">${esc(s.address)}</div>
          <div><span class="eta">ETA ~${fmtClock(v.etas[i + 1])}</span> · ${(v.route.legs[i].distance / 1000).toFixed(1)} km${s.vol > 1 ? ` · vol ${s.vol}` : ''}</div></div></div>`;
      });
    });
    html += '<p class="hint">ETAs include Bengaluru rush-hour factors and unloading time — a guide, not a promise. Drivers navigate with live traffic in Google Maps.</p>';
    side.innerHTML = html;
    map.invalidateSize(); map.fitBounds(L.latLngBounds(allPts).pad(0.12));
    $$('.drv', side).forEach(el => el.onchange = () => { vans[+el.dataset.v].driver_id = el.value; });
    $('#back').onclick = showPicker;
    $('#draft').onclick = e => busy(e.target, () => persist(false)).catch(() => {});
    $('#publish').onclick = e => busy(e.target, () => persist(true)).catch(() => {});
  }

  async function persist(publish) {
    const { vans } = result;
    if (publish && vans.some(v => !v.driver_id)) {
      if (!confirm('Some vans have no driver. Publish anyway?')) return;
    }
    // Re-use the plan row if this result was already saved as a draft.
    result.planId ??= (await createPlan({ plan_date: settings.date, depart_min: result.departMin, unload_min: settings.unload })).id;
    await savePlan(result.planId, vans.map(v => ({
      van_no: v.van_no, driver_id: v.driver_id, color: v.color,
      distance_m: Math.round(v.route.distance), duration_s: Math.round(v.route.duration),
      back_by_min: Math.round(v.etas.at(-1)), geometry: v.route.geometry,
      stops: v.stops.map((s, i) => ({ location_id: s.id, volume: s.vol, early: s.early, eta_min: Math.round(v.etas[i + 1]) })),
    })), publish);
    toast(publish ? 'Published — drivers can see their routes now.' : 'Draft saved.');
    if (publish) location.hash = '#/board';
  }

  showPicker();
  return () => map.destroy();
}
